import React, { useEffect, useState } from "react";
import { MapPin, Search } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import ListingCard from "./ListingCard";
import { get } from "../../utils/request";
import "./things-to-do.scss";

const ThingsToDoSearch = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get("query") || "";
  const selectedCountry = searchParams.get("country") || "All";
  const [keyword, setKeyword] = useState(query);
  const [countryFilter, setCountryFilter] = useState(selectedCountry);
  const [countries, setCountries] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setKeyword(query);
    setCountryFilter(selectedCountry);
  }, [query, selectedCountry]);

  useEffect(() => {
    get("/thingsToDo/countries")
      .then((result) => setCountries(Array.isArray(result) ? result : []))
      .catch(() => setCountries([]));
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    get("/thingsToDo/search", {
      q: query || undefined,
      country: selectedCountry === "All" ? undefined : selectedCountry,
    })
      .then((result) => active && setProducts(Array.isArray(result?.products) ? result.products : []))
      .catch(() => {
        if (active) {
          setProducts([]);
          setError("We could not load Things To Do results right now.");
        }
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [query, selectedCountry]);

  const saveSearch = (event) => {
    event.preventDefault();
    const params = new URLSearchParams();
    params.set("query", keyword.trim());
    if (countryFilter !== "All") params.set("country", countryFilter);
    navigate(`/things-to-do/search?${params.toString()}`);
  };

  const resultLabel = query
    ? `Results for “${query}”`
    : selectedCountry !== "All"
      ? `Things To Do in ${selectedCountry}`
      : "All Things To Do";

  return (
    <>
      <Navbar />
      <main className="things-page pb-4">
        <section className="things-search-hero">
          <div className="container">
            <p>Things To Do</p>
            <h1>Find an experience worth remembering.</h1>
            <form className="things-hero-searchbar" onSubmit={saveSearch}>
              <div className="things-hero-field things-hero-field-full">
                <label htmlFor="things-search-query">Search experiences</label>
                <Search className="things-search-icon" aria-hidden="true" />
                <input
                  id="things-search-query"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="Attraction, activity or destination"
                />
              </div>
              <div className="things-hero-field things-hero-select">
                <label htmlFor="things-search-country">Destination</label>
                <MapPin className="things-search-icon" aria-hidden="true" />
                <select
                  id="things-search-country"
                  value={countryFilter}
                  onChange={(event) => setCountryFilter(event.target.value)}
                >
                  <option value="All">Anywhere</option>
                  {countries.map((country) => <option key={country} value={country}>{country}</option>)}
                </select>
              </div>
              <button type="submit" className="things-hero-search-btn">
                <Search aria-hidden="true" /> <span>Search</span>
              </button>
            </form>
          </div>
        </section>

        <div className="container things-search-results">
          <div className="things-section-heading">
            <div><p>Live catalogue</p><h2 className="things-section-title">{resultLabel}</h2></div>
            {!loading && !error && <span>{products.length} {products.length === 1 ? "experience" : "experiences"}</span>}
          </div>
          {loading && <div className="things-results-status">Searching current experiences…</div>}
          {error && <div className="things-results-status">{error}</div>}
          {!loading && !error && products.length === 0 && (
            <div className="things-results-status">No active experiences match this search.</div>
          )}
          <div className="row g-3">
            {products.map((listing) => (
              <div className="col-md-6 col-lg-4" key={listing.id}>
                <ListingCard listing={listing} />
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default ThingsToDoSearch;
